import { Spinner } from 'flowbite-react';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import GuestSignInModal from 'src/components/placementDrives/GuestSignInModal';
import Container from 'src/components/shared/Container';
import { appBasePath, DateBadge, DRIVE_FONT_STYLE, PrimaryButton, PublicDriveHeader, useDriveFont, BackLink } from 'src/components/placementDrives/shared';
import { useUserContext } from 'src/context/UserProvider';
import { useGetPublicDrive } from 'src/hooks/drives.hooks';

// "Sign in to continue": shown to logged out visitors who tap Apply. Masai learners sign in and come straight back to the apply page.
const PlacementDriveSignInPage = () => {
	useDriveFont();
	const [guestOpen, setGuestOpen] = useState(false);
	const { driveId } = useParams();
	const navigate = useNavigate();
	const { user } = useUserContext();
	const { data, isLoading } = useGetPublicDrive(driveId, !user);

	if (user) return <Navigate to={`/placement-drives/${driveId}/apply`} replace />;
	if (isLoading) {
		return (
			<div className="flex justify-center py-24">
				<Spinner size="xl" />
			</div>
		);
	}
	const drive = data?.drive;
	if (!drive) return <div className="py-24 text-center text-[#555770]">Drive not found.</div>;

	const applyUrl = `${window.location.origin}${appBasePath()}/placement-drives/${drive._id}/apply`;

	return (
		<div className="min-h-screen bg-white" style={DRIVE_FONT_STYLE}>
			<PublicDriveHeader />
			<Container className="dp-fade w-[92%] max-w-[760px] pt-3 pb-8 md:pt-4 md:pb-14">
				<BackLink className="inline-flex" onClick={() => navigate(`/placement-drives/${drive._id}`)}>{drive.name}</BackLink>
				<div className="pt-6 md:pt-14">
				<div className="text-center">
					<h1 className="text-[32px] leading-[36px] md:text-[52px] md:leading-[56px] font-[800] tracking-[-0.04em] text-[#0F1626]">Sign in to continue</h1>
					<p className="mt-2 md:mt-3 text-[14px] md:text-[18px] text-[#555770]">To apply to {drive.name}</p>
				</div>
				<div className="mt-6 md:mt-10">
				<div className="rounded-[20px] border border-[#EEEEF5] bg-white p-4 md:p-5 shadow-[0_2px_12px_rgba(20,20,60,0.05)]">
					<div className="flex items-center gap-4">
						<DateBadge date={drive.driveDate} endDate={drive.driveEndDate} />
						<div className="min-w-0">
							<p className="text-[16px] md:text-[17px] font-[700] text-[#0F1626]">{drive.name}</p>
							<p className="text-[13px] md:text-[14px] text-[#555770]">{drive.companiesCount} companies</p>
							<p className="text-[13px] md:text-[14px] text-[#555770] whitespace-nowrap">
								Application closes {dayjs(drive.applicationCloseDate).format('D MMM')}
							</p>
						</div>
					</div>
					<div className="mt-5 border-t border-[#EEEEF5] pt-5">
						<PrimaryButton
							className="w-full !py-4 !text-[16px]"
							onClick={() => window.location.assign(`${window.location.origin}${appBasePath()}/auth/signin?redirect_url=${encodeURIComponent(applyUrl)}`)}
						>
							Continue with Masai account
						</PrimaryButton>
						<p className="mt-3 text-center text-[13px] text-[#8A8BA3]">Takes you straight back to this drive</p>
					</div>
				</div>

				<div className="mt-4 md:mt-5 flex flex-col gap-4 rounded-[20px] bg-[#F6F6FA] p-4 md:p-5 md:flex-row md:items-center md:justify-between">
					<div>
						<p className="text-[16px] font-[700] text-[#0F1626]">Not a Masai learner?</p>
						<p className="text-[14px] text-[#555770]">Join as a guest with your email.</p>
					</div>
					<button
						onClick={() => setGuestOpen(true)}
						className="w-full shrink-0 rounded-[12px] border border-[#E6E6EE] bg-white px-5 py-3 text-[14px] md:w-auto font-[600] text-[#0F1626] hover:bg-[#FAFAFC]"
					>
						Continue as guest
					</button>
				</div>
				</div>
				</div>
			</Container>
			<GuestSignInModal open={guestOpen} onClose={() => setGuestOpen(false)} driveId={drive._id} />
		</div>
	);
};

export default PlacementDriveSignInPage;
